import React, { useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { Field, change, reduxForm } from 'redux-form';

import { isRequired } from '../../../../utils/validators';
import FormItem from '../../../common/forms/FormItem';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import Select from '../../../common/forms/select/Select';
import TextInput from '../../../common/forms/input/TextInput';

import {
  clearOrganizationCharts,
  getOrganizationCharts,
} from '../../../../actions';

import { DEFAULT_QUERY_GET_ALL } from '../../../../config/queries';
import {
  MAINTENANCE_WARNING_PRIORITY,
  ORGANIZATION_CHART_AREA_TYPES,
} from '../../../../config/constants';

import {
  optionPropTypes,
  optionsPropTypes,
} from '../../../common/forms/select/SelectPropTypes';
import { enumToSelectOptions } from '../../../../utils/enum';
import DynamicForm from '../../../common/forms/DynamicForm';
import { MAINTENANCE_FAIL_MODE_CATEGORY_COLUMNS } from '../../../../config/dynamicFormFields';
import ActivitySelect from '../../../common/forms/select/ActivitySelect';

export const MaintenanceFailModeForm = ({
  handleSubmit,
  loading,
  dispatchGetOrganizationCharts,
  dispatchClearOrganizationCharts,
  organizationCharts,
  loadingOrganizationCharts,
  dispatchChange,
  initialValues,
}) => {
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);

  useLayoutEffect(() => {
    dispatchGetOrganizationCharts({ ...DEFAULT_QUERY_GET_ALL });

    return () => dispatchClearOrganizationCharts();
  }, []);

  const getOrganizationChartElements = (hierarchy, parentId, setState) => {
    const options = organizationCharts.filter(
      ({ companyAreaTypeId, parentCompanyAreaId }) =>
        +companyAreaTypeId === +ORGANIZATION_CHART_AREA_TYPES[hierarchy].id &&
        parentCompanyAreaId === parentId,
    );

    setState(options);
  };

  useLayoutEffect(() => {
    const { categoryId } = initialValues;

    if (organizationCharts.length > 0) {
      const categories = organizationCharts.filter(
        ({ companyAreaTypeId }) =>
          +companyAreaTypeId === +ORGANIZATION_CHART_AREA_TYPES.area.id,
      );

      setCategoryOptions(categories);

      if (categoryId) {
        getOrganizationChartElements(
          'section',
          categoryId.value,
          setSubCategoryOptions,
        );
      }
    }
  }, [organizationCharts]);

  const onChangeCategory = ({ value }) => {
    dispatchChange('MaintenanceFailModeForm', 'subCategoryId', null);

    getOrganizationChartElements('section', value, setSubCategoryOptions);
  };

  if (loading || loadingOrganizationCharts) return <Loader />;

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row>
        <FormItem label="Descripción" required>
          <Field
            name="description"
            component={TextInput}
            placeholder="Descripción"
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Categoría" required>
          <Field
            name="categoryId"
            component={Select}
            options={categoryOptions}
            onChange={onChangeCategory}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Sub Categoría" required>
          <Field
            name="subCategoryId"
            component={Select}
            options={subCategoryOptions}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Prioridad" required>
          <Field
            name="priority"
            component={Select}
            options={enumToSelectOptions(MAINTENANCE_WARNING_PRIORITY)}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Actividad Principal" required>
          <Field
            name="activityId"
            component={ActivitySelect}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row required>
        <FormItem label="Actividades Vinculadas">
          <Field
            name="maintenanceFailModeActivityList"
            component={ActivitySelect}
            isMulti
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <h3>Catergorias</h3>
      <DynamicForm
        name="maintenanceFailModeCategoryList"
        columns={MAINTENANCE_FAIL_MODE_CATEGORY_COLUMNS}
      />

      <FormFooter />
    </Form>
  );
};

MaintenanceFailModeForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchGetOrganizationCharts: PropTypes.func.isRequired,
  dispatchClearOrganizationCharts: PropTypes.func.isRequired,
  organizationCharts: optionsPropTypes,
  loadingOrganizationCharts: PropTypes.bool.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    description: PropTypes.string,
    categoryId: optionPropTypes,
    subCategoryId: optionPropTypes,
    activityId: optionPropTypes,
    priority: optionPropTypes,
  }),
};

MaintenanceFailModeForm.defaultProps = {
  organizationCharts: [],
  initialValues: {},
};

const mapStateToProps = ({ MechanicalMaintenanceUnit }) => ({
  loading: !MechanicalMaintenanceUnit.MaintenanceFailMode.getIn([
    'current',
    'activity',
  ]).isEmpty(),

  organizationCharts: MechanicalMaintenanceUnit.OrganizationChart.getIn([
    'all',
    'content',
    'content',
  ]).map(({ id, name, companyAreaTypeId, parentCompanyAreaId }) => ({
    value: id,
    label: name,
    companyAreaTypeId,
    parentCompanyAreaId,
  })),

  loadingOrganizationCharts: MechanicalMaintenanceUnit.OrganizationChart.getIn([
    'all',
    'loading',
  ]),
});

const mapDispatchToProps = {
  dispatchGetOrganizationCharts: getOrganizationCharts,
  dispatchClearOrganizationCharts: clearOrganizationCharts,
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: 'MaintenanceFailModeForm',
})(MaintenanceFailModeForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
